import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { Option, Aircraft as AircraftOptions } from '../types/database'

// Cache for aircraft options to prevent redundant fetches
const optionsCache = new Map<string, Map<number, Option[]>>()

// Function to invalidate cache for specific aircraft IDs (clears cache entries)
export function invalidateAircraftOptionsCache(aircraftIds: number[]) {
  // Find all cache keys that contain any of the aircraft IDs
  const keysToDelete: string[] = []

  for (const [cacheKey] of optionsCache) {
    const cachedAircraftIds = cacheKey.split(',').map(id => parseInt(id, 10))
    const hasOverlap = aircraftIds.some(id => cachedAircraftIds.includes(id))
    if (hasOverlap) {
      keysToDelete.push(cacheKey)
    }
  }

  keysToDelete.forEach(key => optionsCache.delete(key))
}

export function useAircraftOptions(aircraftIds: number[], refreshTrigger: number = 0) {
  const cacheKey = aircraftIds.sort().join(',')
  const [data, setData] = useState<Map<number, Option[]>>(() => {
    return optionsCache.get(cacheKey) || new Map()
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dataVersion, setDataVersion] = useState(0)

  useEffect(() => {
    if (aircraftIds.length === 0) {
      setData(new Map())
      return
    }

    // Check if we already have cached data
    // Skip cache if refreshTrigger > 0 (means we need to refetch)
    const cached = optionsCache.get(cacheKey)
    if (cached && cached.size > 0 && refreshTrigger === 0) {
      setData(cached)
      setDataVersion(v => v + 1)
      return
    }

    // Clear cache for this key when refreshTrigger changes
    if (refreshTrigger > 0) {
      optionsCache.delete(cacheKey)
    }

    const fetchOptions = async () => {
      setLoading(true)
      setError(null)

      try {
        // Fetch aircraft records with their option IDs
        const { data: aircraftData, error: aircraftError } = await supabase
          .from('aircraft')
          .select('*')
          .in('aircraft_id', aircraftIds)

        if (aircraftError) throw aircraftError

        if (!aircraftData || aircraftData.length === 0) {
          setData(new Map())
          return
        }

        // Collect all unique option IDs
        const allOptionIds = new Set<string>()
        aircraftData.forEach((aircraft: AircraftOptions) => {
          if (aircraft.options && Array.isArray(aircraft.options)) {
            aircraft.options.forEach(optionId => allOptionIds.add(optionId))
          }
        })

        if (allOptionIds.size === 0) {
          // No options assigned to any aircraft
          const emptyMap = new Map<number, Option[]>()
          aircraftData.forEach((aircraft: AircraftOptions) => {
            emptyMap.set(aircraft.aircraft_id, [])
          })
          setData(emptyMap)
          optionsCache.set(cacheKey, emptyMap) // Also cache the empty result
          setDataVersion(v => v + 1)
          return
        }

        // Fetch all options details
        const { data: optionsData, error: optionsError } = await supabase
          .from('options')
          .select('*')
          .in('option_id', Array.from(allOptionIds))

        if (optionsError) throw optionsError

        // Create a map of option_id to Option
        const optionsMap = new Map<string, Option>()
        optionsData?.forEach((option: Option) => {
          optionsMap.set(option.option_id, option)
        })

        // Build the final map of aircraft_id to Option[]
        const resultMap = new Map<number, Option[]>()
        aircraftData.forEach((aircraft: AircraftOptions) => {
          const aircraftOptions: Option[] = []
          if (aircraft.options && Array.isArray(aircraft.options)) {
            aircraft.options.forEach(optionId => {
              const option = optionsMap.get(optionId)
              if (option) {
                aircraftOptions.push(option)
              }
            })
          }
          resultMap.set(aircraft.aircraft_id, aircraftOptions)
        })

        setData(resultMap)
        optionsCache.set(cacheKey, resultMap) // Cache the result
        setDataVersion(v => v + 1) // Increment version to trigger memoization update
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch aircraft options')
      } finally {
        setLoading(false)
      }
    }

    fetchOptions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aircraftIds.join(','), refreshTrigger]) // Use join to create stable dependency, include refreshTrigger

  // Memoize the data to prevent unnecessary re-renders
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedData = useMemo(() => data, [dataVersion])

  return { data: memoizedData, loading, error }
}